"use client";
// @ts-strict-ignore
import Downshift from "downshift";
import { m } from "framer-motion";
import { useRouter } from "i18n/routing";
import SearchIconSvg from "public/static/images/cryptopedia_search.svg";
import * as React from "react";
import {
  SearchContainer,
  SearchIcon,
  SearchInput,
  SuggestContainer,
  SuggestItem,
  SuggestList
} from "./style";
import { connectHits, connectSearchBox } from "react-instantsearch-dom";
import { useRef, useState } from "react";
import useDebounce from "hooks/useDebounce";

export const SearchSuggestions = ({
  active,
  suggestions,
  highlightedIndex,
  getMenuProps,
  getItemProps,
  handleSuggestionClick,
  ...containerProps
}) => (
  <SuggestContainer active={active} {...containerProps}>
    <SuggestList {...getMenuProps()}>
      {suggestions.map((item, index) => (
        <SuggestItem
          {...getItemProps({
            key: item.id,
            index,
            item
          })}
          highlighted={highlightedIndex === index}
          onClick={() => handleSuggestionClick(item.name)}
        >
          {item.name}
        </SuggestItem>
      ))}
    </SuggestList>
  </SuggestContainer>
);

const SearchHits = connectHits(
  ({
    hits,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    active,
    handleSuggestionClick
  }) => {
    return (
      <SearchSuggestions
        active={active}
        suggestions={hits}
        highlightedIndex={highlightedIndex}
        getMenuProps={getMenuProps}
        getItemProps={getItemProps}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }
);

const Form = React.forwardRef(
  (
    { getInputProps, getRootProps, isOpen, handleSubmit, term, ...props }: any,
    ref
  ) => {
    function onsubmit(e, _term) {
      e.preventDefault();
      handleSubmit(_term);
    }
    const inputVariants = {
      open: { width: "100%", opacity: 1 },
      closed: { width: 0, opacity: 0 }
    };

    return (
      <React.Fragment>
        <m.div
          initial="closed"
          animate={isOpen ? "open" : "closed"}
          transition={{ duration: 0.3 }}
          variants={inputVariants}
        >
          <form
            onSubmit={(e) => onsubmit(e, term)}
            {...getRootProps({ refKey: undefined }, { suppressRefError: true })}
          >
            <SearchInput
              ref={ref}
              type="text"
              placeholder={"Search Cryptopedia"}
              {...getInputProps()}
              value={term}
            />
          </form>
        </m.div>
        <SearchHits onSubmit={onsubmit} {...props} />
      </React.Fragment>
    );
  }
);

const DesktopSearch = ({ currentRefinement, refine, isOpen, toggleOpen }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const formRef = useRef(null);
  const isActive = showSuggestions && currentRefinement;
  const [, cancel] = useDebounce(
    () => {
      refine(searchTerm);
      if (document.activeElement === formRef.current) setShowSuggestions(true);
    },
    200,
    [searchTerm]
  );

  const router = useRouter();
  const handleSearch = (term) => {
    const url = `/cryptopedia/search?query=${term}`;
    router.push(url);
  };

  const handleSuggestionClick = (e) => {
    setSearchTerm(e);
    setShowSuggestions(false);
    handleSearch(e);
    formRef?.current?.blur();
  };

  function handleIconClick() {
    setShowSuggestions(false);
    toggleOpen();
    if (!isOpen) formRef?.current?.focus();
  }

  const handleChange = (e) => {
    setSearchTerm(e);
  };

  return (
    <SearchContainer>
      <SearchIcon src={SearchIconSvg.src} onClick={handleIconClick} />
      <Downshift
        onInputValueChange={handleChange}
        itemToString={(item) => (item ? item.name : "")}
        onOuterClick={cancel}
      >
        {(props) => (
          <Form
            ref={formRef}
            term={searchTerm}
            handleSubmit={handleSuggestionClick}
            setShowSuggestions={setShowSuggestions}
            {...props.getRootProps(
              { refKey: null },
              { suppressRefError: true }
            )}
            handleSuggestionClick={handleSuggestionClick}
            active={isActive}
            {...props}
            isOpen={isOpen}
          />
        )}
      </Downshift>
    </SearchContainer>
  );
};

export default connectSearchBox(DesktopSearch);
